import { useEffect } from 'react';
import { jsonLdScript } from '@/lib/seo';

interface SEOHeadProps {
  title: string;
  description?: string;
  canonical?: string;
  schema?: object;
  ogImage?: string;
}

/**
 * Mutates <head> tags on mount. The static prerender baseline copy lives in
 * index.html — this component overrides for sub-routes at runtime.
 *
 * Important: prerender (Vite SSG) re-runs the app's render with each route to
 * capture the resulting HTML. SEOHead's mutations are captured at that time.
 */
export function SEOHead({ title, description, canonical, schema, ogImage }: SEOHeadProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (title) document.title = title;
    updateMeta('description', description);
    updateMeta('og:title', title, 'property');
    updateMeta('og:description', description, 'property');
    if (ogImage) updateMeta('og:image', ogImage, 'property');
    if (canonical) updateLink('canonical', canonical);
    if (schema) updateJsonLd(schema);
  }, [title, description, canonical, schema, ogImage]);

  return null;
}

function updateMeta(name: string, content: string | undefined, attr: 'name' | 'property' = 'name') {
  if (!content) return;
  const existing = document.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (existing.length === 0) {
    const el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
    return;
  }
  existing[0]!.setAttribute('content', content);
  for (let i = 1; i < existing.length; i++) {
    existing[i]!.remove();
  }
}

function updateLink(rel: string, href: string) {
  // Dedupe: prerender may emit a canonical via the plugin's head elements set
  // AND index.html may have a static one. We keep exactly one.
  const existing = document.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (existing.length === 0) {
    const el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('href', href);
    document.head.appendChild(el);
    return;
  }
  // Keep the first, remove any duplicates, update href
  existing[0]!.setAttribute('href', href);
  for (let i = 1; i < existing.length; i++) {
    existing[i]!.remove();
  }
}

function updateJsonLd(schema: object) {
  let el = document.querySelector<HTMLScriptElement>('script[type="application/ld+json"][data-seo]');
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.textContent = jsonLdScript(schema);
}
